import { Box, Typography } from '@mui/material';
import {
  useTranslator,
  withHover,
  withHoverProps,
  CcfButton,
  CcfSmsIcon,
  CcfElevatedEmailIcon,
  CcfElevatedChatIcon,
  CcfElevatedPhoneIcon,
} from '@nice-ccf/ui-controls';
import { Transition } from 'react-transition-group';
import classes from '../ccf-directory.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeContactSelector,
  getContactElevationDetails,
  updateActiveContact,
  updateContactInteraction,
  updateDigitalContactToElevateVoiceControl,
} from '@nice-ccf/ui-components';
import { getInteractionData } from '../../ccf-interaction-workspace/ccf-interaction-mock-data';
import { customCopilotTemplate } from '../../ccf-app-space/ccf-copilot/ccf-copilot-chat/mock-data/custom-flow';
import { useEffect } from 'react';
import { DigitalChannelType } from '@nice-ccf/core-sdk';

type size = 'small' | 'medium' | 'large' | undefined;

/**
 * Component to be used for directory item
 * @param props - Agent
 * @example <DirectoryItem />
 * @returns
 */
const DirectoryItemContact = (
  props: { elevatedContact: any } & withHoverProps
) => {
  const { elevatedContact, isHovering, onMouseEnter, onMouseOut } = props;
  const [translate] = useTranslator();
  const dispatch = useDispatch();
  const activecontact = useSelector(activeContactSelector);
  const contactElevatedDetails = useSelector(getContactElevationDetails);
  const emailElevetedData =
    activecontact?.customerChatReplyJsonData?.length > 0
      ? activecontact?.customerChatReplyJsonData.filter(
          (item: any) => item.messageFrom === 'elevatedEmail'
        )
      : customCopilotTemplate?.copilotAgentAndCustomerChatData.filter(
          (item: any) => item.messageFrom === 'elevatedEmail'
        );
  const smsElevetedData =
    activecontact?.customerChatReplyJsonData?.length > 0
      ? activecontact?.customerChatReplyJsonData.filter(
          (item: any) => item.messageFrom === 'elevatedSMS'
        )
      : customCopilotTemplate?.copilotAgentAndCustomerChatData.filter(
          (item: any) => item.messageFrom === 'elevatedSMS'
        );
  const styles = {
    displayFlex: 'flex',
    flexDirection: 'row',
    paddingX: 1.5,
    paddingY: 1,
    padding: 1.5,
    boxMaxWidth: '100%',
    alignItemsCenter: 'center',
    justifyContentStart: 'flex-start',
    justifyContentBetween: 'space-between',
    size: 'small' as size,
    iconSize: 'small' as size,
  };

  const defaultStyles = {
    height: 60,
    opacity: 1,
    transition: `height 250ms ease-in`,
    background: isHovering ? '#ecf5fa' : 'inherit',
    overflow: 'hidden',
  };

  /**
   * Function to dispatch an action to fetch agent contact group
   * @param e -  React.ChangeEvent
   * @example - dispatcElevatedInteractionAction()
   */
  function dispatchElevatedInteractionAction(
    sourceInteractionID: string,
    interactionType: string
  ) {
    if (interactionType !== 'inboundVoice') {
      const template: any = getInteractionData(interactionType);
      const elevatedInteractionTemplate = {
        id: template.id,
        channelID: template.channelId,
        realExternalPlatformId: template.realExternalPlatformId,
        name: template.name,
        skill: template.skill,
        hasAbilityToQuoteMessage: template.hasAbilityToQuoteMessage,
        hasAbilityToLike: template.hasAbilityToLike,
        hasAbilityToShare: template.hasAbilityToShare,
        isPrivate: template.isPrivate,
        hasAbilityToSendFiles: template.hasAbilityToSendFiles,
        nicknameOnExternalPlatform: template.nicknameOnExternalPlatform,
        originalTweetContent: {},
        agentReply:
          activecontact?.copilotAssist === 'Yes' ? [] : template?.agentReply,
        otherReplies: [],
        isHistoryEmailContent: template?.historyemail,
      };
      if (activecontact?.copilotAssist === 'Yes') {
        const contactElevatedDetailsCopy = new Map();
        if (interactionType === 'Email') {
          contactElevatedDetails?.forEach(function (value: any, key: any) {
            contactElevatedDetailsCopy.set(key, value);
          });
          if (emailElevetedData[0] !== undefined) {
            contactElevatedDetailsCopy.set('email', emailElevetedData[0]);
            dispatch(
              updateActiveContact({
                id: activecontact?.id || '',
                updates: { elevationDetails: contactElevatedDetailsCopy },
              })
            );
          } else {
            elevatedInteractionTemplate.agentReply = template.agentReply;
          }
        } else if (interactionType === 'sms') {
          contactElevatedDetails?.forEach(function (value: any, key: any) {
            contactElevatedDetailsCopy.set(key, value);
          });
          if (smsElevetedData[0] !== undefined) {
            contactElevatedDetailsCopy.set('sms', smsElevetedData[0]);
            dispatch(
              updateActiveContact({
                id: activecontact?.id || '',
                updates: { elevationDetails: contactElevatedDetailsCopy },
              })
            );
          } else {
            elevatedInteractionTemplate.agentReply = template.agentReply;
          }
        }
      }

      dispatch(
        updateContactInteraction({
          id: sourceInteractionID,
          interaction: elevatedInteractionTemplate,
        })
      );
    } else {
      dispatch(
        updateDigitalContactToElevateVoiceControl({ id: sourceInteractionID })
      );
    }
  }

  return (
    <Transition timeout={{ enter: 500, exit: 0 }}>
      {(state) => (
        <Box
          style={{
            ...defaultStyles,
          }}
          component={'li'}
          className={classes.directoryItemContact}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseOut}
        >
          <Box className={classes.directoryelevatedContact}>
            <Typography noWrap className={classes.contactName}>
              {elevatedContact?.customer
                ? `${[
                    elevatedContact?.customer?.firstName,
                    elevatedContact?.customer?.middleName,
                    elevatedContact?.customer?.lastName,
                  ]
                    .filter(Boolean)
                    .join(' ')}`
                : ''}
            </Typography>
          </Box>
          <Box className={classes.directoryItemelevatedContactStatus}>
            {isHovering && (
              <Box
                display={styles.displayFlex}
                justifyContent={styles.justifyContentStart}
                flex="1"
                ml={1}
              >
                {elevatedContact?.media === 'Digital' && (
                  <CcfButton
                    id="agent_directory_media_elevatePhoneIcon"
                    color="secondary"
                    classes={{ textSecondary: classes.textSecondary }}
                    size={styles.size}
                    onClick={() =>
                      dispatchElevatedInteractionAction(
                        elevatedContact?.id,
                        'inboundVoice'
                      )
                    }
                  >
                    <CcfElevatedPhoneIcon
                      style={{
                        width: '24px',
                        height: '24px',
                        marginTop: '10px',
                        marginLeft: '10PX',
                      }}
                    />
                  </CcfButton>
                )}
                <CcfButton
                  id="agent_directory_media_elevateSmsIcon"
                  color="secondary"
                  classes={{ textSecondary: classes.textSecondary }}
                  size={styles.size}
                  onClick={() =>
                    dispatchElevatedInteractionAction(
                      elevatedContact?.id,
                      'sms'
                    )
                  }
                >
                  <CcfSmsIcon style={{ width: '20px', height: '20px' }} />
                </CcfButton>
                {(elevatedContact?.media === 'Voice' ||
                  elevatedContact?.media === 'Digital') &&
                  elevatedContact?.channel !== 'Chat' && (
                    <CcfButton
                      id="agent_directory_media_elevateChatIcon"
                      color="secondary"
                      classes={{ textSecondary: classes.textSecondary }}
                      size={styles.size}
                      onClick={() =>
                        dispatchElevatedInteractionAction(
                          elevatedContact?.id,
                          'Chat'
                        )
                      }
                    >
                      <CcfElevatedChatIcon
                        style={{
                          width: '24px',
                          height: '24px',
                          marginTop: '10px',
                        }}
                      />
                    </CcfButton>
                  )}
                {(elevatedContact?.media === 'Voice' ||
                  elevatedContact?.media === 'Digital') &&
                  elevatedContact?.channel !== 'Email' && (
                    <CcfButton
                      id="agent_directory_media_elevateEmailIcon"
                      color="secondary"
                      classes={{ textSecondary: classes.textSecondary }}
                      onClick={() =>
                        dispatchElevatedInteractionAction(
                          elevatedContact?.id,
                          'Email'
                        )
                      }
                    >
                      <CcfElevatedEmailIcon
                        style={{
                          width: '24px',
                          height: '24px',
                          marginTop: '10px',
                        }}
                      />
                    </CcfButton>
                  )}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Transition>
  );
};

export default withHover(DirectoryItemContact);
